import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { type FieldErrorProps, useFieldError } from "Hooks/useFieldError";
import { isNoU } from "Utils/ObjectUtils";
import type * as React from "react";
import { forwardRef, type JSX } from "react";
import { IMaskInput } from "react-imask";
import styled from "styled-components";

type Props = TextFieldProps &
  FieldErrorProps & {
    label?: string;
    name?: string;
    mask?: string | NumberConstructor | RegExp;
    isMaskLazy?: boolean;
    maskPlaceholder?: string;
    unmask?: boolean | "typed";
    max?: number;
    min?: number;
    onMaskInputChange?: (val: string) => void;
    onInputChange?:
      | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
      | undefined;
    inputStartAdornment?: JSX.Element;
    inputEndAdornment?: JSX.Element;
    maxLength?: number;
    scale?: number;
    unit?: string;
    isGreyIfEmpty?: boolean;
    disableHelperText?: boolean;
    isReadOnly?: boolean;
  };

const StyledInput = styled(TextField)<{ $isEmpty: boolean }>`
  margin-top: ${(props) => props.theme.spacing(1)};

  .MuiInputBase-input {
    /* padding-bottom: ${(props) => props.theme.spacing(1)}; */
    color: ${(props) =>
      props.$isEmpty
        ? props.theme.colors.border
        : props.theme.palette.text.primary};
    font-size: 16px;
    font-weight: 500;

    &:-webkit-autofill,
    &:-webkit-autofill:hover &:-webkit-autofill:focus {
      box-shadow: 0 0 0px 1000px
        ${(props) => props.theme.palette.background.default} inset;
      color: ${(props) => props.theme.palette.text.primary};
    }
    &::placeholder {
      color: ${(props) => props.theme.colors.placeholderGray};
      opacity: 1;
    }
  }

  .MuiInputBase-root {
    &::before {
      border-bottom: 1px solid ${(props) => props.theme.colors.border};
    }
    min-height: 44px;
  }

  .MuiInputLabel-root {
    font-size: 18px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${(props) => props.theme.colors.border};
  }

  .MuiOutlinedInput-multiline {
    padding: 0;
  }

  .MuiFormHelperText-root {
    margin: 0;
    min-height: 20px;
  }

  .Mui-disabled {
    &.MuiInputBase-formControl {
      background-color: ${(props) => props.theme.colors.gray}80;

      .MuiInputBase-input {
        color: ${(props) => props.theme.palette.text.primary}80;
      }

      .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.colors.border};
      }
    }
  }
`;

const StyledInputWrapper = styled.div<{ isEmpty: boolean }>`
  display: flex;
  flex-flow: column;

  .input-wrapper {
    position: relative;
  }

  input {
    color: ${(props) =>
      props.isEmpty
        ? props.theme.colors.border
        : props.theme.palette.text.primary};
  }
`;

const StyledUnit = styled.div`
  position: absolute;
  pointer-events: none;
  top: calc(50% - 20px);
  right: 20px;
  opacity: 0.2;
`;

const IMaskInputWrapper = forwardRef<HTMLInputElement>((props, ref) => (
  <IMaskInput {...props} inputRef={ref} />
));

export const TextInput = (props: Props) => {
  const { hasError, errorMessage, originalProps } = useFieldError(props);
  const {
    label,
    name,
    mask,
    isMaskLazy,
    maskPlaceholder,
    unmask,
    max,
    min,
    onMaskInputChange,
    onInputChange,
    value,
    inputStartAdornment,
    inputEndAdornment,
    maxLength,
    scale,
    unit,
    isGreyIfEmpty,
    helperText,
    disableHelperText,
    type,
    isReadOnly,
    ...rest
  } = originalProps;

  const handleMaskInputChange = (nextValue: string) => {
    onMaskInputChange?.(nextValue);
  };

  const isEmpty =
    (isGreyIfEmpty && value === "") || value === null || value === undefined;

  return (
    <StyledInputWrapper isEmpty={isEmpty}>
      <div>{label && <label htmlFor={name}>{label}</label>}</div>
      <div className="input-wrapper">
        {isNoU(mask) ? (
          <StyledInput
            name={name}
            id={name}
            variant="outlined"
            // label={label}
            fullWidth
            error={hasError}
            value={value}
            helperText={
              disableHelperText
                ? undefined
                : ((helperText || errorMessage) ?? " ")
            }
            slotProps={{
              input: {
                readOnly: isReadOnly,
                startAdornment: inputStartAdornment,
                endAdornment: inputEndAdornment,
                inputProps: { maxLength, max, min },
              },
            }}
            onChange={onInputChange}
            $isEmpty={isEmpty}
            multiline={type === "textarea"}
            type={type}
            {...rest}
          />
        ) : (
          <StyledInput
            name={name}
            id={name}
            variant="outlined"
            fullWidth
            error={hasError}
            value={value}
            helperText={
              disableHelperText
                ? undefined
                : ((helperText || errorMessage) ?? " ")
            }
            slotProps={{
              input: {
                readOnly: isReadOnly,
                startAdornment: inputStartAdornment,
                endAdornment: inputEndAdornment,
                // biome-ignore lint/suspicious/noExplicitAny: types too complex, not worth it
                inputComponent: IMaskInputWrapper as any,
                inputProps: {
                  mask,
                  lazy: isMaskLazy ?? true,
                  placeholderChar: maskPlaceholder ?? "_",
                  unmask: unmask ?? true,
                  radix: ",",
                  mapToRadix: ["."],
                  thousandsSeparator: " ",
                  max: max ?? Number.MAX_SAFE_INTEGER,
                  min: min ?? Number.MIN_SAFE_INTEGER,
                  onAccept: handleMaskInputChange,
                  scale,
                  maxLength,
                },
              },
            }}
            $isEmpty={isEmpty}
            type={type}
            {...rest}
          />
        )}
        {unit && <StyledUnit>{unit}</StyledUnit>}
      </div>
    </StyledInputWrapper>
  );
};
